"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-background/60 rounded-2xl border border-border">
        <p className="text-foreground font-default text-xl italic">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-lg px-6">
          Send another message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] tracking-[0.2em] uppercase font-default text-muted-foreground font-bold">
                Your Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-primary font-default text-base text-foreground placeholder:text-muted-foreground/60" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" rules={{
        required: "Family or organization name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] tracking-[0.2em] uppercase font-default text-muted-foreground font-bold">
                Family / Organization
              </FormLabel>
              <FormControl>
                <Input placeholder="The Smith Family" className="border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-primary font-default text-base text-foreground placeholder:text-muted-foreground/60" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] tracking-[0.2em] uppercase font-default text-muted-foreground font-bold">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-primary font-default text-base text-foreground placeholder:text-muted-foreground/60" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] tracking-[0.2em] uppercase font-default text-muted-foreground font-bold">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555-123-4567" className="border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-primary font-default text-base text-foreground placeholder:text-muted-foreground/60" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_description" rules={{
      required: "Please tell us how we can help"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] tracking-[0.2em] uppercase font-default text-muted-foreground font-bold">
              How can we support you?
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your needs, interests, or how you'd like to partner with us..." className="border-0 border-b border-border bg-transparent px-0 min-h-32 focus-visible:ring-0 focus-visible:border-primary font-default text-base text-foreground placeholder:text-muted-foreground/60 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget_range" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] tracking-[0.2em] uppercase font-default text-muted-foreground font-bold mb-3">
              Primary Interest
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="flex flex-col sm:flex-row gap-4 sm:gap-8 pt-2">
                {["Respite", "Advocacy", "Programs"].map((option, i) => <label key={option} data-index={i} className="flex items-center gap-3 cursor-pointer group">
                    <RadioGroupItem value={option} className="border-muted-foreground text-primary data-[state=checked]:border-primary size-5" />
                    <span className="font-default text-base text-foreground group-hover:text-primary transition-colors">
                      {option}
                    </span>
                  </label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="flex justify-end pt-4">
        <Button type="submit" disabled={isSubmitting} className="rounded-lg px-8 py-6 h-auto font-default text-base bg-secondary text-secondary-foreground hover:bg-primary shadow-md transition-colors">
          {isSubmitting ? "Sending..." : "Send Message"}
          <ArrowRight className="ml-2 size-4" />
        </Button>
      </div>
    </Form>;
}
export default function StartYourJourneyWithUs() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const headlineWords1 = ["Let's", "light"];
  const headlineWords2 = ["the", "way."];
  return <section data-section-id="4512" id="start-your-journey-with-us" className="dark bg-background py-24 md:py-40 px-6 md:px-12 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className="lg:col-start-1 lg:col-span-5 flex flex-col justify-center">
            <p className={`text-xs tracking-[0.3em] uppercase font-default text-secondary mb-8 transition-all duration-700 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              — Join Our Community
            </p>

            <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground mb-10">
              <span className="block">
                {headlineWords1.map((word, i) => <span key={i} data-index={i} className={`inline-block mr-3 transition-all duration-700 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
                transitionDelay: `${i * 120 + 200}ms`
              }}>
                    {word}
                  </span>)}
              </span>
              <span className="block italic text-secondary">
                {headlineWords2.map((word, i) => <span key={i} data-index={i} className={`inline-block mr-3 transition-all duration-700 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
                transitionDelay: `${i * 120 + 500}ms`
              }}>
                    {word}
                  </span>)}
              </span>
            </h2>

            <p className={`font-default text-lg md:text-xl leading-relaxed text-muted-foreground max-w-md mb-12 transition-all duration-700 delay-700 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              Hope Torchlighters is here to support neurodiverse families through respite, advocacy, and inclusive community programs. Reach out to start your journey with us today.
            </p>

            <div className={`flex items-center gap-8 transition-all duration-700 delay-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Link to="mailto:hello@hopetorchlighters.org" className="font-default italic text-base text-foreground hover:text-secondary transition-colors">
                hello@hopetorchlighters.org
              </Link>
              <div className="h-8 w-px bg-border" aria-hidden="true" />
              <Link to="tel:+15551234567" className="font-default italic text-base text-foreground hover:text-secondary transition-colors">
                +1 (555) 123-4567
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-start-7 lg:col-span-6 transition-all duration-1000 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="bg-card rounded-2xl shadow-lg border border-border p-8 md:p-12">
              <FormProvider formKey="start_journey_form_home" formId="start_journey_form_home" sectionName="start-your-journey-with-us" defaultValues={{
              name: "",
              company: "",
              email: "",
              phone: "",
              project_description: "",
              budget_range: ""
            }} validationRules={{
              name: {
                required: true
              },
              company: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              project_description: {
                required: true
              }
            }} submitText="Send Message" successMessage="Thank you for reaching out. We are honored to connect with you and will be in touch soon." errorMessage="Something went wrong. Please try again or contact us directly.">
                <InquiryForm />
              </FormProvider>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}